import { css, CSSObject } from '@emotion/react';
import { CSSProperties, useRef } from 'react';
import {
  MarginProps,
  PaddingProps,
  Responsive,
  ResponsiveMarginProps,
  ResponsivePaddingProps,
} from '~/design';
import { FullTheme, useFullTheme } from '~/design/theme';
import merge from 'lodash/merge';
import { Breakpoint, down, up } from '~/design/breakpoint';

export type CSS = {
  [Key in keyof CSSProperties]: CSSProperties[Key] | Responsive<CSSProperties[Key]>;
};

/**
 * Provides a set of responsive utilities such as {@link responsiveCss|css} and {@link responsiveMap|map}.
 */
export function useResponsive() {
  const { breakpoints } = useFullTheme();
  return {
    css: (css: CSS) => responsiveCss(css, breakpoints),
    map: responsiveMap,
  };
}

/**
 * Converts responsive values to properly use media breakpoints.  See tests for
 * examples
 *
 * Responsive values in this case means a value that conforms to the
 * ResponsiveValue interface
 *
 * TODO: make this support :hover and :focus later if needed
 *
 * @param css The CSS properties that optionally includes responsive values
 * @param breakpoints The theme breakpoints
 * @returns The CSSObject with responsive values changed to use media breakpoints
 * @example
 * // returns { '@media (min-width: 0px)': { gap: 4 }, '@media (min-width: 600px)': { gap: 8 } }
 * responsiveCss({ gap: { xs: 4, sm: 8 } });
 * // returns { gap: 4 }
 * responsiveCss({ gap: 4 });
 */
export function responsiveCss(css: CSS, breakpoints: FullTheme['breakpoints']) {
  let newCss: CSSObject = {};

  for (const breakpointKey of ['xs', 'sm', 'md', 'lg', 'xl'] as const) {
    for (const cssPropertyName in css) {
      const cssPropertyValue = css[cssPropertyName as keyof CSSProperties];
      if (isResponsiveValue(cssPropertyValue, breakpoints)) {
        if (!cssPropertyValue[breakpointKey]) {
          continue;
        }

        newCss = merge(newCss, {
          [`@media (min-width: ${breakpoints[breakpointKey]}px)`]: {
            [cssPropertyName]: cssPropertyValue[breakpointKey],
          },
        });
      } else {
        newCss[cssPropertyName] = cssPropertyValue;
      }
    }
  }

  return newCss;
}

/**
 * Converts a responsive prop a map of breakpoints to CSS property values.
 * If a non-responsive prop is given, converts it to a single CSS property with no breakpoints.
 *
 * @param prop either a non-responsive prop or a responsive prop
 * @param func a function to converts a component prop to a CSS property value
 * @returns a map of breakpoints to CSS property values ()
 * @example
 * // returns { xs: '8px', sm: '16px' }
 * responsiveMap({ xs: 1, sm: 2 }, prop => `${prop * 8}px`);
 * // returns '16px'
 * responsiveMap(2, prop => `${prop * 8}px`);
 * // returns undefined
 * responsiveMap(undefined, prop => prop && `${prop * 8}px`);
 */
export function responsiveMap<I, O>(
  prop: I | Responsive<I>,
  func: (p: I) => O,
): O | Responsive<O> | undefined {
  if (!prop) return undefined;

  if (Array.isArray(prop)) {
    return func(prop);
  }

  if (typeof prop === 'object') {
    return Object.entries(prop).reduce((prev, [bp, value]) => ({ ...prev, [bp]: func(value) }), {});
  }

  return func(prop);
}

function isResponsiveValue<T>(
  val: unknown | Responsive<T>,
  breakpoints: FullTheme['breakpoints'],
): val is Responsive<T> {
  return Boolean(
    val && typeof val === 'object' && Object.keys(breakpoints).some(key => key in val),
  );
}

/**
 * Generates an effectively unique ID
 *
 * @see https://stackoverflow.com/a/2117523
 */
export function shortUUID(): string {
  const longId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

  return longId.replace(/-/g, '');
}

/**
 * React hook that returns a unique ID.  Useful for generating IDs for aria
 * purposes.
 */
export function useId(): string {
  const idRef = useRef<string | null>(null);
  if (!idRef.current) {
    idRef.current = `x${shortUUID()}`;
  }

  return idRef.current;
}

export function useMarginStyle(marginProps: Partial<MarginProps>) {
  const { spacing } = useFullTheme();
  return css({
    margin: marginProps.m && spacing[marginProps.m],
    marginInline: marginProps.mx && spacing[marginProps.mx],
    marginBlock: marginProps.my && spacing[marginProps.my],
    marginTop: marginProps.mt && spacing[marginProps.mt],
    marginRight: marginProps.mr && spacing[marginProps.mr],
    marginBottom: marginProps.mb && spacing[marginProps.mb],
    marginLeft: marginProps.ml && spacing[marginProps.ml],
  });
}

export function useResponsiveMarginStyle(marginProps: Partial<ResponsiveMarginProps>) {
  const responsive = useResponsive();
  const { spacing } = useFullTheme();
  return responsive.css({
    margin: marginProps.m && responsive.map(marginProps.m, prop => spacing[prop]),
    marginInline: marginProps.mx && responsive.map(marginProps.mx, prop => spacing[prop]),
    marginBlock: marginProps.my && responsive.map(marginProps.my, prop => spacing[prop]),
    marginTop: marginProps.mt && responsive.map(marginProps.mt, prop => spacing[prop]),
    marginRight: marginProps.mr && responsive.map(marginProps.mr, prop => spacing[prop]),
    marginBottom: marginProps.mb && responsive.map(marginProps.mb, prop => spacing[prop]),
    marginLeft: marginProps.ml && responsive.map(marginProps.ml, prop => spacing[prop]),
  });
}

export function usePaddingStyle(paddingProps: Partial<PaddingProps>) {
  const { spacing } = useFullTheme();
  return css({
    padding: paddingProps.p && spacing[paddingProps.p],
    paddingInline: paddingProps.px && spacing[paddingProps.px],
    paddingBlock: paddingProps.py && spacing[paddingProps.py],
    paddingTop: paddingProps.pt && spacing[paddingProps.pt],
    paddingRight: paddingProps.pr && spacing[paddingProps.pr],
    paddingBottom: paddingProps.pb && spacing[paddingProps.pb],
    paddingLeft: paddingProps.pl && spacing[paddingProps.pl],
  });
}

export function useResponsivePaddingStyle(paddingProps: Partial<ResponsivePaddingProps>) {
  const responsive = useResponsive();
  const { spacing } = useFullTheme();
  return responsive.css({
    padding: paddingProps.p && responsive.map(paddingProps.p, prop => spacing[prop]),
    paddingInline: paddingProps.px && responsive.map(paddingProps.px, prop => spacing[prop]),
    paddingBlock: paddingProps.py && responsive.map(paddingProps.py, prop => spacing[prop]),
    paddingTop: paddingProps.pt && responsive.map(paddingProps.pt, prop => spacing[prop]),
    paddingRight: paddingProps.pr && responsive.map(paddingProps.pr, prop => spacing[prop]),
    paddingBottom: paddingProps.pb && responsive.map(paddingProps.pb, prop => spacing[prop]),
    paddingLeft: paddingProps.pl && responsive.map(paddingProps.pl, prop => spacing[prop]),
  });
}

export function useResponsiveHideStyle({
  hideBelow,
  hideAbove,
}: {
  hideBelow?: Breakpoint;
  hideAbove?: Breakpoint;
}) {
  const styles: Record<string, { display: 'none' }> = {};

  if (hideBelow) {
    styles[down(hideBelow)] = {
      display: 'none',
    };
  }

  if (hideAbove) {
    styles[up(hideAbove)] = {
      display: 'none',
    };
  }

  return css(styles);
}

export const svgVerticalAlign = css(`
  svg {
    vertical-align: baseline;
  }
`);
